import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { Fragment, Component, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Overlay from '../Overlay';
import Header from './Header';
import ScrollDirection from './ScrollDirection';
import Form from '../Form';
import ScrollableElementFormContext from '../Form/ScrollableElementFormContext';
import DrawerProgress from './DrawerProgress';
var propTypes = {
  /**
   * Toggles visibility.
   * @since 0.0.65
   */
  isOpen: PropTypes.bool,

  /**
   * The title of the `Drawer`.
   * @since 0.0.65
   */
  title: PropTypes.node.isRequired,

  /**
   * The subtitle of the `Drawer`.
   * @since 0.0.65
   */
  subtitle: PropTypes.node,

  /**
   * Show the minimize button and the inactive close button.
   * @since 1.4.1
   */
  hideButton: PropTypes.bool,

  /**
   * Show back button in the header. If true the close button is not show.
   * @since 0.0.65
   */
  backButton: PropTypes.bool,

  /**
   * Overlay placement.
   * @since 0.0.65
   */
  placement: PropTypes.oneOf(['left', 'right']),

  /**
   * Overlay size.
   * @since 0.0.65
   */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),

  /**
   * onClose handler.
   * @since 0.0.65
   */
  onClose: PropTypes.func,

  /**
   * Wraps children into [Form](#!/Form) if not empty.
   * @since 0.0.66
   */
  form: PropTypes.object,

  /**
   * Content of the `Drawer`.
   * @since 0.0.65
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string,

  /**
   * Adds [Progress](#!/Progress) into footer.
   * @since 1.11.0
   */
  progress: PropTypes.shape({
    /**
     * A title for the progress.
     */
    title: PropTypes.string,

    /**
     * An array of [ProgressStep](#!/ProgressStep) props.
     */
    steps: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      status: PropTypes.string,
      progress: PropTypes.number,
      icon: PropTypes.string
    })).isRequired,

    /**
     * An option that controls drawer closing ability.
     */
    cancelable: PropTypes.bool,

    /**
     * On overall status event handler.
     * @since 3.8.0
     */
    onStatusChange: PropTypes.func,

    /**
     * @ignore
     */
    onClose: PropTypes.func,

    /**
     * @ignore
     */
    onClosableChange: PropTypes.func
  })
};
var defaultProps = {
  isOpen: false,
  subtitle: '',
  backButton: undefined,
  hideButton: undefined,
  placement: 'left',
  size: 'md',
  onClose: null,
  form: null,
  children: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "drawer"),
  progress: null
};
/**
 * `Drawer` component is used for working with properties of an object (or system) without leaving the context of the object.
 * The component is similar to `Dialog` but it is intended to work with more complex forms or lists.
 * @since 0.0.65
 */

var Drawer =
/*#__PURE__*/
function (_Component) {
  _inherits(Drawer, _Component);

  function Drawer() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Drawer);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Drawer)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      closable: true
    });

    _defineProperty(_assertThisInitialized(_this), "scrollableRef", createRef());

    _defineProperty(_assertThisInitialized(_this), "handleClosableChange", function (closable) {
      _this.setState({
        closable: closable
      });
    });

    return _this;
  }

  _createClass(Drawer, [{
    key: "takeApart",
    value: function takeApart(_render) {
      var _this$props = this.props,
          form = _this$props.form,
          progress = _this$props.progress,
          onClose = _this$props.onClose,
          baseClassName = _this$props.baseClassName;
      var children = this.props.children;

      if (progress) {
        progress.onClose = onClose;
        progress.onClosableChange = this.handleClosableChange;
      }

      var createProgress = function createProgress() {
        return React.createElement(DrawerProgress, progress);
      };

      if (form) {
        form.className = classNames("".concat(baseClassName, "__form"), form.className);

        if (form.cancelButton !== false) {
          form.cancelButton = _objectSpread({
            onClick: onClose
          }, form.cancelButton);
        }

        return React.createElement(Form, _extends({}, form, {
          render: function render(_ref) {
            var renderForm = _ref.renderForm,
                renderActionButtons = _ref.renderActionButtons;
            return _render({
              content: renderForm(),
              footer: progress ? createProgress() : renderActionButtons()
            });
          }
        }), children);
      }

      children = React.createElement("div", {
        className: "".concat(baseClassName, "__inner-body")
      }, children);

      if (progress) {
        return _render({
          content: children,
          footer: createProgress()
        });
      }

      return _render({
        content: children
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          baseClassName = _this$props2.baseClassName,
          className = _this$props2.className,
          children = _this$props2.children,
          title = _this$props2.title,
          subtitle = _this$props2.subtitle,
          backButton = _this$props2.backButton,
          hideButton = _this$props2.hideButton,
          onClose = _this$props2.onClose,
          form = _this$props2.form,
          progress = _this$props2.progress,
          props = _objectWithoutProperties(_this$props2, ["baseClassName", "className", "children", "title", "subtitle", "backButton", "hideButton", "onClose", "form", "progress"]);

      var closable = this.state.closable;
      return React.createElement(Overlay, _extends({
        className: classNames(baseClassName, _defineProperty({}, "".concat(baseClassName, "--form"), form), className),
        onClose: onClose,
        canCloseOnBackdropClick: false
      }, props), this.takeApart(function (_ref2) {
        var content = _ref2.content,
            footer = _ref2.footer;
        return React.createElement(Fragment, null, React.createElement(Header, {
          backButton: backButton,
          hideButton: typeof hideButton === 'boolean' ? hideButton : Boolean(progress && !closable),
          onClose: onClose,
          "data-skip-auto-focus": true
        }, title), React.createElement(ScrollDirection, null, function (_ref3) {
          var direction = _ref3.direction,
              handleScroll = _ref3.handleScroll;
          return React.createElement("div", {
            className: "".concat(baseClassName, "__body"),
            onScroll: handleScroll,
            ref: _this2.scrollableRef
          }, React.createElement(ScrollableElementFormContext.Provider, {
            value: _this2.scrollableRef.current
          }, React.createElement("div", null, subtitle && React.createElement("div", {
            className: classNames("".concat(baseClassName, "__subheader"), _defineProperty({}, "".concat(baseClassName, "__subheader--hidden"), direction === 'down'))
          }, React.createElement("h4", {
            className: "".concat(baseClassName, "__subheader-title")
          }, subtitle)), content)));
        }), footer && React.createElement("footer", {
          className: "".concat(baseClassName, "__footer")
        }, footer));
      }));
    }
  }]);

  return Drawer;
}(Component);

Drawer.propTypes = propTypes;
Drawer.defaultProps = defaultProps;
export default Drawer;